import type { JSX } from 'react';
import { AvatarBlock, FormGroup } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IRecipient } from '@common/recipient';
import type { IOrganization, IPractitioner } from '@frontend/components/interfaces';
import { ProcessActivityStatus } from '@common/enums';
import { getDisplayName } from '@common/utils/getDisplayName';
import { getOneOrganization } from '@frontend/services/api/organizationsApiService';
import { getOnePractitioner } from '@frontend/services/api/practitionersApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';

type SelectProcessActivityPerformerFormGroupProps = Readonly<{
    activityStatus: ProcessActivityStatus;
    recipient: IRecipient;
}>;

export function SelectProcessActivityPerformerFormGroup(
    props: SelectProcessActivityPerformerFormGroupProps
): JSX.Element | null {
    const abortSignal = useAbortSignal();
    const [recipientPractitioner, setRecipientPractitioner] = useState<IPractitioner | null>(null);
    const [recipientOrganization, setRecipientOrganization] = useState<IOrganization | null>(null);

    useEffect(() => {
        if (props.recipient.organizationId) {
            loadOrganization(props.recipient.organizationId);
        }
    }, [props.recipient.organizationId]);

    useEffect(() => {
        if (props.recipient.practitionerId) {
            loadPractitioner(props.recipient.practitionerId);
        }
    }, [props.recipient.practitionerId]);

    if (!recipientPractitioner && !recipientOrganization) {
        return null;
    }

    return (
        <FormGroup
            label={
                props.activityStatus === ProcessActivityStatus.complete
                    ? getSelectedLanguage().common.sentTo
                    : getSelectedLanguage().common.willSendTo
            }
        >
            <AvatarBlock
                icon={props.recipient.practitionerId ? 'patient-nogender' : 'organization'}
                size="small"
                subtitle={recipientPractitioner ? getDisplayName(recipientPractitioner) : undefined}
                title={recipientOrganization?.name}
            />
        </FormGroup>
    );

    async function loadOrganization(organizationId: string) {
        const organization = await getOneOrganization(organizationId, abortSignal);
        setRecipientOrganization(organization);
    }

    async function loadPractitioner(practitionerId: string) {
        const practitioner = await getOnePractitioner(practitionerId, abortSignal);
        setRecipientPractitioner(practitioner);
    }
}
